import React from 'react';
import {
  Box,
  Button,
  Popper,
  Grow,
  Paper,
  MenuItem,
  ButtonProps,
  ClickAwayListener,
  useTheme,
  alpha,
} from '@mui/material';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import {  useNavigate } from 'react-router-dom';
import { useNavigationDropdown } from '@/hooks/useNavigationDropdown';
import { getNavigationStyles } from '@/styles/components/navigation';
import { NavigationItem } from '@/navigation/config';

interface NavigationDropdownProps extends Omit<ButtonProps, 'children'> {
  item: NavigationItem;
  isActive?: boolean;
  hasDarkHero?: boolean;
  scrolled?: boolean;
  onNavigate?: (path: string) => void;
}

export const NavigationDropdown: React.FC<NavigationDropdownProps> = ({
  item,
  isActive,
  hasDarkHero = false,
  scrolled,
  onNavigate,
  ...buttonProps
}) => {
  const theme = useTheme();
  const styles = getNavigationStyles(theme);
  const navigate = useNavigate();
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  
  const {
    isOpen,
    onMouseEnter,
    onMouseLeave,
    onClose,
  } = useNavigationDropdown({
    closeDelay: 50,
    openDelay: 50,
  });

  const hasChildren = Array.isArray(item.children) && item.children.length > 0;

  const handleClick = () => {
    if (!hasChildren) {
      if (item.isExternal) {
        window.open(item.path, '_blank', 'noopener,noreferrer');
      } else {
        navigate(item.path);
        if (onNavigate) {
          onNavigate(item.path);
        } else {
          window.scrollTo(0, 0);
        }
      }
    }
  };

  const getTextColor = () => {
    if (hasDarkHero && !scrolled) return 'white';
    return theme.palette.text.primary;
  };

  return (
    <Box
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={styles.dropdown.container}
    >
      <Button
        ref={anchorRef}
        onClick={handleClick}
        endIcon={hasChildren ? (
          <ExpandMore
            sx={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: 0.7,
              color: getTextColor(),
            }}
          />
        ) : undefined}
        sx={{
          ...styles.button.nav,
          ...(isActive && {
            opacity: 1,
            background: alpha(theme.palette.action.selected, 0.15),
          }),
          color: getTextColor(),
          position: 'relative',
          '&::after': isActive ? {
            content: '""',
            position: 'absolute',
            bottom: 6,
            left: 12,
            right: 12,
            height: 2,
            borderRadius: '2px',
            backgroundColor: hasDarkHero && !scrolled
              ? 'white'
              : alpha(theme.palette.common.black, 0.2),
          } : {},
          '&:hover': {
            opacity: 1,
            background: alpha(theme.palette.action.hover, 0.1),
            color: theme.palette.primary.main,
            '& .MuiSvgIcon-root': {
              transform: hasChildren ? 'rotate(180deg)' : 'none',
              color: theme.palette.primary.main,
            },
          },
        }}
        {...buttonProps}
      >
        {item.title}
      </Button>

      {hasChildren && (
        <Popper
          open={isOpen}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          transition
          disablePortal
          style={{ zIndex: theme.zIndex.appBar + 1 }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                padding: 16,
                altAxis: true,
              },
            },
          ]}
        >
          {({ TransitionProps }) => (
            <Grow
              {...TransitionProps}
              style={{ transformOrigin: 'top left' }}
              timeout={150}
            >
              <Paper
                elevation={0}
                sx={{
                  ...styles.dropdown.paper,
                  ...styles.animations.fadeIn,
                }}
              >
                <ClickAwayListener onClickAway={onClose}>
                  <Box>
                    {item.children?.map((child) => 
                      child.isExternal ? (
                        <MenuItem
                          key={child.path}
                          component="a"
                          href={child.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={onClose}
                          sx={styles.dropdown.item}
                        >
                          {child.icon && (
                            <child.icon 
                              sx={{ 
                                color: 'inherit',
                                opacity: 0.7,
                              }} 
                            />
                          )}
                          {child.title}
                          <ChevronRight 
                            sx={{
                              ...styles.dropdown.icon,
                              color: 'inherit',
                              transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                        </MenuItem>
                      ) : (
                        <MenuItem
                          key={child.path}
                          onClick={() => {
                            navigate(child.path);
                            onClose();
                            if (onNavigate) {
                              onNavigate(child.path);
                            } else {
                              window.scrollTo(0, 0);
                            }
                          }}
                          sx={styles.dropdown.item}
                        >
                          {child.icon && (
                            <child.icon 
                              sx={{ 
                                color: 'inherit',
                                opacity: 0.7,
                              }} 
                            />
                          )}
                          {child.title}
                          <ChevronRight 
                            sx={{
                              ...styles.dropdown.icon,
                              color: 'inherit',
                              transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                        </MenuItem>
                      )
                    )}
                  </Box>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </Box>
  );
};

export default NavigationDropdown; 